import { useState, useEffect, useCallback } from "react";
import { STANDARD_PARTS_CATALOG, stdPartDocId, stdPartName, type StandardPartDefinition } from "./standardParts";
import { getStepParts, saveStepPart, deleteStepPart, type StepLibraryEntry } from "./stepLibrary";

interface PartDoc {
  id: string;
  name: string;
  part_number?: string;
  doc_type?: string;
  updated_at?: string;
}

export interface PartPickerResult {
  id: string;
  name: string;
  script?: string;
  configuration?: Record<string, number | string>;
}

interface PartPickerProps {
  assemblyDocId: string;
  onSelect: (doc: PartPickerResult) => void;
  onClose: () => void;
}

// Distinct SVG icons per standard part type
const STD_PART_ICONS: Record<string, string> = {
  "hex-bolt": "M5.5 2h5L12 3.5v2L10.5 7h-5L4 5.5v-2L5.5 2zM8 7v7M6.5 14h3",
  "hex-nut": "M5.5 3h5L12 4.5v3L10.5 9h-5L4 7.5v-3L5.5 3zM8 6a1.2 1.2 0 100 0z",
  "flat-washer": "M4 6.5a4 1.5 0 108 0M4 6.5a4 1.5 0 008 0M4 6.5v1a4 1.5 0 008 0v-1",
  "socket-head-cap-screw": "M5 2h6a1 1 0 011 1v3H4V3a1 1 0 011-1zM6.5 6v8M9.5 6v8M6.5 14h3",
  "ball-bearing": "M8 2a6 6 0 100 12A6 6 0 008 2zM8 5a3 3 0 100 6A3 3 0 008 5z",
  "roller-bearing": "M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM8 4.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7z",
  "thrust-bearing": "M3 5h10M3 5a5 2 0 0010 0M3 11h10M3 11a5 2 0 0010 0",
};

// Human-readable dimension labels
const DIM_LABELS: Record<string, string> = {
  d: "Diameter", af: "Across Flats", hh: "Head Height", len: "Length",
  h: "Height", di: "Inner Dia", d_out: "Outer Dia", t: "Thickness",
  hd: "Head Dia",
};

export default function PartPicker({ assemblyDocId, onSelect, onClose }: PartPickerProps) {
  const [tab, setTab] = useState<"documents" | "standard" | "uploaded">("documents");
  const [parts, setParts] = useState<PartDoc[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/documents")
      .then((res) => res.json())
      .then((docs: PartDoc[]) => {
        setParts(docs.filter((d) => (d.doc_type === "part" || d.doc_type === "assembly") && d.id !== assemblyDocId));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [assemblyDocId]);

  const filtered = search
    ? parts.filter((p) =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        (p.part_number ?? "").toLowerCase().includes(search.toLowerCase())
      )
    : parts;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="part-picker-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="part-picker-header">
          <h3>Add Part to Assembly</h3>
          <button className="part-picker-close" onClick={onClose}>&times;</button>
        </div>

        {/* Tab bar */}
        <div className="part-picker-tabs">
          <button className={`part-picker-tab ${tab === "documents" ? "active" : ""}`} onClick={() => setTab("documents")}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="13" height="13">
              <path d="M3 5l5-3 5 3v6l-5 3-5-3V5z"/><path d="M3 5l5 3 5-3M8 8v6"/>
            </svg>
            My Parts
          </button>
          <button className={`part-picker-tab ${tab === "standard" ? "active" : ""}`} onClick={() => setTab("standard")}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="13" height="13">
              <circle cx="8" cy="5" r="3"/><rect x="7" y="8" width="2" height="6" rx="0.3"/>
            </svg>
            Standard
          </button>
          <button className={`part-picker-tab ${tab === "uploaded" ? "active" : ""}`} onClick={() => setTab("uploaded")}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="13" height="13">
              <path d="M8 2v8M5 7l3 3 3-3"/><path d="M3 12h10"/>
            </svg>
            Uploaded
          </button>
        </div>

        {tab === "documents" && (
          <>
            <input
              className="part-picker-search"
              type="text"
              placeholder="Search parts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            <div className="part-picker-list">
              {loading && <div className="part-picker-empty">Loading...</div>}
              {!loading && filtered.length === 0 && (
                <div className="part-picker-empty">No parts found</div>
              )}
              {filtered.map((p) => (
                <button key={p.id} className="part-picker-item" onClick={() => onSelect({ id: p.id, name: p.name })}>
                  {p.doc_type === "assembly" ? (
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="20" height="20">
                      <rect x="2" y="4" width="5" height="8" rx="0.5" /><rect x="9" y="2" width="5" height="8" rx="0.5" /><path d="M7 8h2" strokeDasharray="1 0.5" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="20" height="20">
                      <path d="M3 5l5-3 5 3v6l-5 3-5-3V5z" /><path d="M3 5l5 3 5-3M8 8v6" />
                    </svg>
                  )}
                  <div className="part-picker-item-info">
                    <span className="part-picker-item-name">{p.name}</span>
                    <span className="part-picker-item-pn">{p.part_number ?? ""}</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {tab === "standard" && (
          <StandardPartsTab onSelect={onSelect} />
        )}

        {tab === "uploaded" && (
          <UploadedPartsTab onSelect={onSelect} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Standard Parts Tab
// ---------------------------------------------------------------------------

function StandardPartsTab({ onSelect }: { onSelect: (doc: PartPickerResult) => void }) {
  const [selectedPart, setSelectedPart] = useState<StandardPartDefinition | null>(null);
  const [sizeIndex, setSizeIndex] = useState(0);
  const [extras, setExtras] = useState<Record<string, number | string>>({});

  const handleSelectPart = (part: StandardPartDefinition) => {
    setSelectedPart(part);
    setSizeIndex(part.defaultSizeIndex);
    const defaults: Record<string, number | string> = {};
    for (const f of part.extraFields ?? []) defaults[f.key] = f.default;
    setExtras(defaults);
  };

  const handleInsert = () => {
    if (!selectedPart) return;
    const size = selectedPart.sizes[sizeIndex];
    const params = { ...size.params, ...extras };
    const script = selectedPart.scriptTemplate(params);
    const name = stdPartName(selectedPart, size, extras);
    const id = stdPartDocId(selectedPart.id, size.label);
    onSelect({ id, name, script, configuration: { ...extras, size: size.label } });
  };

  if (selectedPart) {
    const size = selectedPart.sizes[sizeIndex];
    const iconPath = STD_PART_ICONS[selectedPart.id] ?? "";
    return (
      <div className="std-part-config">
        <button className="std-part-back" onClick={() => setSelectedPart(null)}>
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="12" height="12">
            <path d="M10 3L5 8l5 5"/>
          </svg>
          Back to catalog
        </button>

        {/* Part header with icon */}
        <div className="std-part-config-header">
          <div className="std-part-config-icon">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1" width="36" height="36">
              <path d={iconPath}/>
            </svg>
          </div>
          <div>
            <div className="std-part-config-title">{selectedPart.name}</div>
            <div className="std-part-config-subtitle">ISO Metric — {size.label}</div>
          </div>
        </div>

        {/* Size chips instead of native select */}
        <div className="std-part-field">
          <label>Size</label>
          <div className="std-part-size-chips">
            {selectedPart.sizes.map((s, i) => (
              <button
                key={s.label}
                className={`std-part-size-chip ${i === sizeIndex ? "active" : ""}`}
                onClick={() => setSizeIndex(i)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {(selectedPart.extraFields ?? []).map((f) => (
          <div key={f.key} className="std-part-field">
            <label>{f.label}</label>
            <input
              type="number"
              className="std-part-number"
              min={f.min} max={f.max} step={f.step}
              value={extras[f.key] ?? f.default}
              onChange={(e) => setExtras({ ...extras, [f.key]: Number(e.target.value) })}
            />
            <span className="std-part-unit">mm</span>
          </div>
        ))}

        {/* Readable dimension summary */}
        <div className="std-part-preview-dims">
          {Object.entries(size.params).map(([k, v]) => (
            <span key={k} className="std-part-dim">
              {DIM_LABELS[k] ?? k}: {extras[k] ?? v}mm
            </span>
          ))}
        </div>

        <button className="std-part-insert-btn" onClick={handleInsert}>
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
            <path d="M8 3v10M3 8h10"/>
          </svg>
          Insert {stdPartName(selectedPart, size, extras)}
        </button>
      </div>
    );
  }

  const categories: { key: string; label: string; icon: string }[] = [
    { key: "fastener", label: "Fasteners", icon: "M8 3a2.5 2.5 0 100 5M7 8v6M9 8v6M6.5 14h3" },
    { key: "bearing", label: "Bearings", icon: "M8 2a6 6 0 100 12A6 6 0 008 2zM8 5a3 3 0 100 6A3 3 0 008 5z" },
  ];

  return (
    <div className="part-picker-list">
      {categories.map((cat) => {
        const parts = STANDARD_PARTS_CATALOG.filter((p) => p.category === cat.key);
        if (parts.length === 0) return null;
        return (
          <div key={cat.key}>
            <div className="std-part-category">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="11" height="11">
                <path d={cat.icon}/>
              </svg>
              {cat.label}
            </div>
            {parts.map((part) => (
              <button key={part.id} className="part-picker-item" onClick={() => handleSelectPart(part)}>
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="20" height="20">
                  <path d={STD_PART_ICONS[part.id] ?? ""}/>
                </svg>
                <div className="part-picker-item-info">
                  <span className="part-picker-item-name">{part.name}</span>
                  <span className="part-picker-item-pn">{part.sizes.length} sizes</span>
                </div>
                <svg className="part-picker-item-arrow" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="12" height="12">
                  <path d="M6 4l4 4-4 4"/>
                </svg>
              </button>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Uploaded Parts Tab (STEP Library)
// ---------------------------------------------------------------------------

function UploadedPartsTab({ onSelect }: { onSelect: (doc: PartPickerResult) => void }) {
  const [entries, setEntries] = useState<StepLibraryEntry[]>([]);
  const [uploading, setUploading] = useState(false);

  const refresh = useCallback(async () => {
    setEntries(await getStepParts());
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleUpload = useCallback(async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".step,.stp,.STEP,.STP";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setUploading(true);
      try {
        const buffer = await file.arrayBuffer();
        const name = file.name.replace(/\.(step|stp)$/i, "");
        await saveStepPart(name, buffer);
        await refresh();
      } finally {
        setUploading(false);
      }
    };
    input.click();
  }, [refresh]);

  const handleDelete = useCallback(async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteStepPart(id);
    await refresh();
  }, [refresh]);

  return (
    <div className="part-picker-list">
      <div className="std-part-category">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="11" height="11">
          <path d="M8 2v8M5 7l3 3 3-3"/><path d="M3 12h10"/>
        </svg>
        Uploaded STEP Files
      </div>

      <button className="step-upload-btn" onClick={handleUpload} disabled={uploading}>
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
          <path d="M8 10V3M5 5l3-3 3 3"/><path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2"/>
        </svg>
        {uploading ? "Uploading..." : "Upload STEP File"}
      </button>

      {entries.length === 0 && (
        <div className="part-picker-empty">No uploaded parts yet</div>
      )}

      {entries.map((entry) => (
        <button key={entry.id} className="part-picker-item" onClick={() => onSelect({ id: `step:${entry.id}`, name: entry.name })}>
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" width="20" height="20">
            <path d="M4 2h5l3 3v9H4V2z"/><path d="M9 2v3h3"/>
          </svg>
          <div className="part-picker-item-info">
            <span className="part-picker-item-name">{entry.name}</span>
            <span className="part-picker-item-pn">{new Date(entry.timestamp).toLocaleDateString()}</span>
          </div>
          <button className="step-delete-btn" onClick={(e) => handleDelete(entry.id, e)} title="Remove from library">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="12" height="12">
              <path d="M4 4l8 8M12 4l-8 8"/>
            </svg>
          </button>
        </button>
      ))}
    </div>
  );
}
